import React from 'react';

/**
 * Lays KPI cards out in a flex row that distributes the available width evenly across however
 * many cards are rendered, wrapping to additional lines only when they no longer fit. Prefer
 * this over a fixed-column grid for a row that stands on its own, so adding or removing a card
 * never leaves an orphan card on its own line (e.g. a 5th KPI under a `lg:grid-cols-4` grid).
 * A row that continues another row of cards further down the same screen is the exception: it
 * stands on that row's grid, because two grids under each other read as cards of two widths
 * aligned with nothing. Falsy children are ignored, so conditionally-rendered cards don't create
 * empty slots. Each card stretches to the height of the tallest card in its row.
 */
export function GovernanceAnalyticsKpiCardRow({ children, className }: { children: React.ReactNode; className?: string }) {
  const cards = React.Children.toArray(children).filter(Boolean);

  return (
    <div className={`flex flex-wrap gap-4 mb-6 ${className ?? ''}`}>
      {cards.map((card, index) => (
        <div
          key={index}
          className={`grow basis-[200px] min-w-[200px] motion-safe:animate-bp-fade-up ${CARD_ENTRANCE_DELAYS[Math.min(index, CARD_ENTRANCE_DELAYS.length - 1)]}`}
        >
          {card}
        </div>
      ))}
    </div>
  );
}

/** The cards of a row arrive one after another, left to right. Literal classes so Tailwind emits them. */
const CARD_ENTRANCE_DELAYS = ['[--bp-delay:0ms]', '[--bp-delay:60ms]', '[--bp-delay:120ms]', '[--bp-delay:180ms]', '[--bp-delay:240ms]'];
